import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSet } from '@/shared/lib/hooks/useSet';
import { useMobileDetect } from '@/shared/lib/hooks/useMobileDetect';
import { FloatingContext } from './floatingContext';

interface FloatingProviderProps {
  children?: ReactNode;
}

const SCROLLBAR_WIDTH = 0;
const PADDING_PROPERTY = 'padding-right';

export const FloatingProvider = ({ children }: FloatingProviderProps) => {
  const elementsSet = useSet<string>([]);
  const { isDesktop } = useMobileDetect();
  const [scrollLockTarget, setScrollLockTarget] = useState<HTMLElement | null>(null);
  const elementsSetRef = useRef(elementsSet);
  const scrollLockTargetRef = useRef(scrollLockTarget);

  elementsSetRef.current = elementsSet;
  scrollLockTargetRef.current = scrollLockTarget;

  useEffect(() => {
    const target = scrollLockTarget ?? document.body;
    const locked = elementsSet.size > 0;

    document.documentElement.style.removeProperty('overflow');
    document.body.style.removeProperty('overflow');
    if (isDesktop()) document.body.style.removeProperty(PADDING_PROPERTY);
    if (scrollLockTarget) scrollLockTarget.style.removeProperty('overflow');

    if (locked) {
      target.style.setProperty('overflow', 'hidden');
      if (target === document.body && isDesktop()) {
        document.body.style.setProperty(PADDING_PROPERTY, `${SCROLLBAR_WIDTH}px`);
      }
    }
  }, [elementsSet.size, isDesktop, scrollLockTarget]);

  useEffect(() => {
    const stripDocumentOverflow = () => {
      const html = document.documentElement;
      const body = document.body;
      if (html.style.getPropertyValue('overflow') === 'hidden') {
        html.style.removeProperty('overflow');
      }
      if (body.style.getPropertyValue('overflow') === 'hidden') {
        body.style.removeProperty('overflow');
      }
      if (body.style.getPropertyValue('overflow-y') === 'hidden') {
        body.style.removeProperty('overflow-y');
      }
    };

    const observer = new MutationObserver(() => {
      stripDocumentOverflow();
    });

    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['style'] });
    observer.observe(document.body, { attributes: true, attributeFilter: ['style'] });
    stripDocumentOverflow();

    return () => observer.disconnect();
  }, []);

  const appendElement = useCallback((elementId: string) => {
    elementsSetRef.current.add(elementId);
  }, []);

  const removeElement = useCallback((elementId: string) => {
    elementsSetRef.current.delete(elementId);
  }, []);

  const providedValue = useMemo(
    () => ({
      appendElement,
      removeElement,
      setScrollLockTarget,
    }),
    [appendElement, removeElement],
  );

  return <FloatingContext.Provider value={providedValue}>{children}</FloatingContext.Provider>;
};
